import { useState, useCallback, useEffect } from 'react'
import usePreferredInventory from '../../../hooks/usePreferredInventory'
import usePermissions from '../../../hooks/usePermissions'
import { Permission } from '../../../graphql'
import { validateNeededByDate, isExpedite } from '../utils/dateValidation'

const VALID_EMAIL_REGEX = /^[\w\-.]+\+?[\w\-.]*@([\w-]+\.)+[\w-]{2,}$/

// The contact phone is the requester's own number, so it persists globally
// (not per-branch like ccEmail) and is reused across every demand request.
export const CONTACT_PHONE_STORAGE_KEY = 'demandRequest/contactPhone'

// Used when a branch's Fleet business address isn't where the requesting
// location can actually accept deliveries (e.g. an on-site yard). Persisted in
// localStorage per branch so a yard doesn't have to retype it every request.
export type AlternateDeliveryAddress = {
	line1: string,
	line2: string,
	city: string,
	state: string,
	postalCode: string,
}

const getEmptyAlternateAddress = (): AlternateDeliveryAddress => ( {
	line1: '',
	line2: '',
	city: '',
	state: '',
	postalCode: '',
} )

type StoredAlternateAddress = {
	enabled: boolean,
	address: AlternateDeliveryAddress,
}

const alternateAddressStorageKey = ( branchId: string ) => `demandRequest/alternateAddress/branch/${branchId}`

const getStoredAlternateAddress = ( branchId: string ): StoredAlternateAddress | null => {
	const raw = localStorage.getItem( alternateAddressStorageKey( branchId ) )
	if ( !raw ) {
		return null
	}
	try {
		const parsed = JSON.parse( raw )
		return {
			enabled: !!parsed.enabled,
			address: { ...getEmptyAlternateAddress(), ...parsed.address },
		}
	} catch {
		return null
	}
}

export const setStoredAlternateAddress = (
	branchId: string,
	enabled: boolean,
	address: AlternateDeliveryAddress,
): void => {
	if ( !branchId ) {
		return
	}
	localStorage.setItem( alternateAddressStorageKey( branchId ), JSON.stringify( { enabled, address } ) )
}

// Stricter than the API, on purpose. The API zero-pads anything up to 5 digits
// because Fleet stores branch ZIPs as integers (1040 is Holyoke, MA), so a
// typed `1234` would be silently saved as `01234`. A person typing an address
// enters the leading zero, so anything short of 5 digits here is a typo.
export const isValidPostalCode = ( postalCode: string ): boolean => /^\d{5}(-\d{4})?$/.test( postalCode.trim() )

const validateAlternateAddress = (
	enabled: boolean,
	address: AlternateDeliveryAddress,
): { isInvalid: boolean, error?: string } => {
	if ( !enabled ) {
		return { isInvalid: false }
	}
	if ( !address.line1.trim() || !address.city.trim() || !address.state.trim() || !address.postalCode.trim() ) {
		return { isInvalid: true, error: 'Address line 1, city, state, and postal code are required' }
	}
	if ( !isValidPostalCode( address.postalCode ) ) {
		return { isInvalid: true, error: 'ZIP code must be 5 digits (12345) or ZIP+4 (12345-6789)' }
	}
	return { isInvalid: false }
}

// Line Item interface based on requirements
export type DemandRequestLineItem = {
	id?: string, // for dynamic array management
	productId: string,
	quantity: number,
	targetType: 'WORK_ORDER' | 'INVOICE' | 'ORDER' | '',
	targetId: string,
	allowSubstitution: boolean, // Now per line item instead of per request
	willCall: boolean, // Now per line item instead of per request
	neededByDate?: string,
	notes?: string,
	selectedPart?: any, // Store the selected Part object for display (not sent to API)
}

// Form Data interface from requirements
export type DemandRequestFormData = {
	requestingInventoryId: string,
	// The DC picked, '' for Automatic. Only a pick: NewRequest resolves it against
	// the requesting location's DC options (main DC when no mini-DC serves it).
	fulfillingInventoryId: string,
	contactPhone: string,
	notes: string,
  ccEmail: string,
	deliverToAlternateAddress: boolean,
	alternateAddress: AlternateDeliveryAddress,
	lineItems: DemandRequestLineItem[],
}

// Validation state for each field
export type FormValidation = {
	requestingInventoryId: {
		isInvalid: boolean,
		error?: string,
	},
	contactPhone: {
		isInvalid: boolean,
		error?: string,
	},
	notes: {
		isInvalid: boolean,
		error?: string,
	},
  ccEmail: {
		isInvalid: boolean,
    error?: string,
  },
	alternateAddress: {
		isInvalid: boolean,
		error?: string,
	},
	lineItems: {
		isInvalid: boolean,
		error?: string,
	},
	permissions: {
		isInvalid: boolean,
		error?: string,
	},
}

export const requiresTargetId = ( targetType: DemandRequestLineItem['targetType'] ): boolean =>
	targetType === 'WORK_ORDER' || targetType === 'INVOICE' || targetType === 'ORDER'

export const TARGET_ID_ERROR_MESSAGES: Record<'WORK_ORDER' | 'INVOICE' | 'ORDER', string> = {
	WORK_ORDER: 'Please enter a valid Work Order number',
	INVOICE: 'Please enter a valid Invoice number',
	ORDER: 'Please enter a valid Order number',
}

// An expedite (next-day or 2nd-day "Needs by") requires the branch to
// identify a business reason: an Invoice, Order, or WO#. Stock (or no
// selection) is not a valid reason for an expedite.
export const NEEDS_BUSINESS_REASON_ERROR =
	'An Invoice, Order, or WO# is required when requesting next-day or 2nd-day delivery'

export const hasUnsatisfiedBusinessReason = ( item: Pick<DemandRequestLineItem, 'targetType' | 'neededByDate'> ): boolean =>
	isExpedite( item.neededByDate ) && !requiresTargetId( item.targetType )

const getEmptyLineItem = (): DemandRequestLineItem => ( {
	id: `line-item-${Date.now()}-${Math.random().toString( 36 ).substr( 2, 9 )}`,
	productId: '',
	quantity: 0,
	targetType: '',
	targetId: '',
	allowSubstitution: true, // Default to true as per schema defaultValue
	willCall: false, // Default to false for will-call
	neededByDate: '',
	notes: ''
} )

const getInitialFormData = (): DemandRequestFormData => ( {
	requestingInventoryId: '',
	fulfillingInventoryId: '',
	contactPhone: localStorage.getItem( CONTACT_PHONE_STORAGE_KEY ) ?? '',
	notes: '',
	ccEmail: '',
	deliverToAlternateAddress: false,
	alternateAddress: getEmptyAlternateAddress(),
	lineItems: [ getEmptyLineItem() ]
} )

const getInitialValidation = (): FormValidation => ( {
	requestingInventoryId: { isInvalid: false },
	contactPhone: { isInvalid: false },
	notes: { isInvalid: false },
	ccEmail: { isInvalid: false },
	alternateAddress: { isInvalid: false },
	lineItems: { isInvalid: false },
	permissions: { isInvalid: false }
} )

// E.164 phone number validation regex (for US numbers: +1 followed by 10 digits)
const E164_PHONE_REGEX = /^\+1\d{10}$/

export type UseDemandRequestFormReturn = {
	// Form data
	formData: DemandRequestFormData,
	validation: FormValidation,
	isValid: boolean,
	// Human-readable list of what still needs attention before the form can submit
	missingRequirements: string[],

	// Form field setters
	setRequestingInventoryId: ( value: string ) => void,
	setFulfillingInventoryId: ( value: string ) => void,
	setContactPhone: ( value: string ) => void,
  validateContactPhoneOnBlur: ( value: string ) => void,
  setCCEmail: ( value: string ) => void,
  validateCCEmailOnBlur: ( value: string ) => void,
	setNotes: ( value: string ) => void,
	setDeliverToAlternateAddress: ( value: boolean ) => void,
	setAlternateAddressField: ( field: keyof AlternateDeliveryAddress, value: string ) => void,

	// Line items management
	lineItems: DemandRequestLineItem[],
	addLineItem: () => void,
	removeLineItem: ( index: number ) => void,
	updateLineItem: ( index: number, updates: Partial<DemandRequestLineItem> ) => void,

	// Computed values
	totalQuantity: number,

	// Form management
	resetForm: () => void,
	validateForm: () => boolean,
}

export function useDemandRequestForm(): UseDemandRequestFormReturn {
	const { preferredInventory } = usePreferredInventory()
	const [ formData, setFormData ] = useState<DemandRequestFormData>( getInitialFormData )
	const [ validation, setValidation ] = useState<FormValidation>( getInitialValidation )

	// Check permissions for creating demand requests
	const { userPermissions, loading: permissionsLoading } = usePermissions( [ Permission.CanCreateDemandRequestByStoreId ] )

	// Initialize form with preferred inventory if available
	useEffect( () => {
		if ( preferredInventory && !formData.requestingInventoryId ) {
			setFormData( ( prev ) => ( {
				...prev,
				requestingInventoryId: String( preferredInventory.id )
			} ) )
		}
	}, [ preferredInventory, formData.requestingInventoryId ] )

	// Validation functions
	const validateContactPhone = ( phone: string ): { isInvalid: boolean, error?: string } => {
		// Contact phone is required (Numina requires a phone on fulfillment orders)
		if ( !phone.trim() ) {
			return { isInvalid: true, error: 'Contact phone is required' }
		}
		if ( !E164_PHONE_REGEX.test( phone ) ) {
			return { isInvalid: true, error: 'Please enter a valid 10-digit US phone number' }
		}
		return { isInvalid: false }
	}

	const validateNotes = ( notes: string ): { isInvalid: boolean, error?: string } => {
		if ( notes.length > 1000 ) {
			return { isInvalid: true, error: 'Notes cannot exceed 1000 characters' }
		}
		return { isInvalid: false }
	}

	const validateCCEmail = ( email: string ): { isInvalid: boolean, error?: string } => {
		if ( email.trim() && !VALID_EMAIL_REGEX.test( email ) ) {
			return { isInvalid: true, error: 'Please enter a valid email address' }
		}
		if ( email.trim() && !email.endsWith( '@equipmentshare.com' ) ) {
			return { isInvalid: true, error: 'Email must end with @equipmentshare.com' }
		}
		return { isInvalid: false }
	}

	const validateLineItems = ( lineItems: DemandRequestLineItem[] ): { isInvalid: boolean, error?: string } => {
		// Must have at least one line item in the array
		if ( lineItems.length === 0 ) {
			return { isInvalid: true, error: 'At least one line item must be specified' }
		}

		// Filter out completely blank line items (no productId and no quantity)
		const nonBlankLineItems = lineItems.filter( ( item ) => item.productId || ( item.quantity && item.quantity > 0 ) )

		// At least one non-blank line item is required for submission
		if ( nonBlankLineItems.length === 0 ) {
			return { isInvalid: true, error: 'At least one line item must have a product and quantity' }
		}

		for ( const item of nonBlankLineItems ) {
			if ( !item.productId ) {
				return { isInvalid: true, error: 'All line items must have a product selected' }
			}
			if ( !item.quantity || item.quantity < 1 ) {
				return { isInvalid: true, error: 'All line items must have a valid quantity (minimum 1)' }
			}
			if ( item.notes && item.notes.length > 1000 ) {
				return { isInvalid: true, error: 'Line item notes cannot exceed 1000 characters' }
			}
			if ( hasUnsatisfiedBusinessReason( item ) ) {
				return { isInvalid: true, error: NEEDS_BUSINESS_REASON_ERROR }
			}
			if ( requiresTargetId( item.targetType ) && !item.targetId ) {
				return { isInvalid: true, error: TARGET_ID_ERROR_MESSAGES[ item.targetType as 'WORK_ORDER' | 'INVOICE' | 'ORDER' ] }
			}
			// Validate neededByDate if provided
			if ( item.neededByDate ) {
				const dateValidation = validateNeededByDate( item.neededByDate )
				if ( !dateValidation.isValid ) {
					return { isInvalid: true, error: dateValidation.error }
				}
			}
		}

		return { isInvalid: false }
	}

	const validatePermissions = (): { isInvalid: boolean, error?: string } => {
		// Permissions validation removed to match UI behavior where any fulfilling inventory can be selected
		return { isInvalid: false }
	}

	const validateForm = useCallback( (): boolean => {
		const newValidation: FormValidation = {
			requestingInventoryId: {
				isInvalid: !formData.requestingInventoryId,
				error: !formData.requestingInventoryId ? 'Requesting inventory is required' : undefined
			},
			contactPhone: validateContactPhone( formData.contactPhone ),
			notes: validateNotes( formData.notes ),
			ccEmail: validateCCEmail( formData.ccEmail ),
			alternateAddress: validateAlternateAddress( formData.deliverToAlternateAddress, formData.alternateAddress ),
			lineItems: validateLineItems( formData.lineItems ),
			permissions: validatePermissions()
		}

		setValidation( newValidation )

		return !Object.values( newValidation ).some( ( field ) => field.isInvalid )
	}, [ formData, userPermissions, permissionsLoading ] )

	// Field setters with validation
	const setRequestingInventoryId = useCallback( ( value: string ) => {
		const ccEmail = value ? localStorage.getItem( `demandRequest/ccEmail/branch/${value}` ) ?? '' : ''
		const storedAlternateAddress = value ? getStoredAlternateAddress( value ) : null

		setFormData( ( prev ) => ( {
			...prev,
			ccEmail,
			requestingInventoryId: value,
			deliverToAlternateAddress: storedAlternateAddress?.enabled ?? false,
			alternateAddress: storedAlternateAddress?.address ?? getEmptyAlternateAddress(),
		} ) )
		setValidation( ( prev ) => ( {
			...prev,
			requestingInventoryId: {
				isInvalid: !value,
				error: !value ? 'Requesting inventory is required' : undefined
			}
		} ) )
	}, [] )

	const setFulfillingInventoryId = useCallback( ( value: string ) => {
		setFormData( ( prev ) => ( { ...prev, fulfillingInventoryId: value } ) )
	}, [] )

	const setContactPhone = useCallback( ( value: string ) => {
		// PhoneInput component handles E.164 formatting automatically
		setFormData( ( prev ) => ( { ...prev, contactPhone: value } ) )
	}, [] )

	const validateContactPhoneOnBlur = useCallback( ( value: string ) => {
		const phoneValidation = validateContactPhone( value )
		setValidation( ( prev ) => ( {
			...prev,
			contactPhone: phoneValidation
		} ) )
	}, [] )

	const setCCEmail = useCallback( ( value: string ) => {
		setFormData( ( prev ) => ( { ...prev, ccEmail: value } ) )
	}, [] )

	const validateCCEmailOnBlur = useCallback( ( value: string ) => {
		const emailValidation = validateCCEmail( value )
		setValidation( ( prev ) => ( {
			...prev,
			ccEmail: emailValidation
		} ) )
	}, [] )

	const setNotes = useCallback( ( value: string ) => {
		setFormData( ( prev ) => ( { ...prev, notes: value } ) )
		const notesValidation = validateNotes( value )
		setValidation( ( prev ) => ( {
			...prev,
			notes: notesValidation
		} ) )
	}, [] )

	const setDeliverToAlternateAddress = useCallback( ( value: boolean ) => {
		setFormData( ( prev ) => ( { ...prev, deliverToAlternateAddress: value } ) )
	}, [] )

	const setAlternateAddressField = useCallback( ( field: keyof AlternateDeliveryAddress, value: string ) => {
		setFormData( ( prev ) => ( { ...prev, alternateAddress: { ...prev.alternateAddress, [ field ]: value } } ) )
	}, [] )

	// Line items management
	const addLineItem = useCallback( () => {
		setFormData( ( prev ) => ( {
			...prev,
			lineItems: [ ...prev.lineItems, getEmptyLineItem() ]
		} ) )
		// Clear line items validation error when user makes changes
		setValidation( ( prev ) => ( {
			...prev,
			lineItems: { isInvalid: false }
		} ) )
	}, [] )

	const removeLineItem = useCallback( ( index: number ) => {
		setFormData( ( prev ) => ( {
			...prev,
			lineItems: prev.lineItems.filter( ( _, i ) => i !== index )
		} ) )
		// Clear line items validation error when user makes changes
		setValidation( ( prev ) => ( {
			...prev,
			lineItems: { isInvalid: false }
		} ) )
	}, [] )

	const updateLineItem = useCallback( ( index: number, updates: Partial<DemandRequestLineItem> ) => {
		setFormData( ( prev ) => ( {
			...prev,
			lineItems: prev.lineItems.map( ( item, i ) =>
				( i === index ? { ...item, ...updates } : item ) )
		} ) )
		// Clear line items validation error when user makes changes
		setValidation( ( prev ) => ( {
			...prev,
			lineItems: { isInvalid: false }
		} ) )
	}, [] )

	// Form reset
	const resetForm = useCallback( () => {
		const initialData = getInitialFormData()
		// Keep preferred inventory if available
		if ( preferredInventory ) {
			initialData.requestingInventoryId = String( preferredInventory.id )
		}
		setFormData( initialData )
		setValidation( getInitialValidation() )
	}, [ preferredInventory ] )

	// Calculate if form is valid
	const hasUnsatisfiedTargetIds = formData.lineItems.some(
		( item ) => requiresTargetId( item.targetType ) && !item.targetId
	)

	// Expedite (next-day or 2nd-day) requests must have a business reason (Invoice/Order/WO#)
	// selected as "Item is for" - Stock (or no selection) doesn't satisfy it.
	const hasUnsatisfiedBusinessReasons = formData.lineItems.some( hasUnsatisfiedBusinessReason )

	// Derived fresh from formData every render (rather than from stored validation
	// state) so it can never go stale relative to the alternateAddress fields
	// or the deliverToAlternateAddress toggle.
	const alternateAddressValidation = validateAlternateAddress( formData.deliverToAlternateAddress, formData.alternateAddress )

	const isValid = !Object.values( validation ).some( ( field ) => field.isInvalid ) &&
		formData.requestingInventoryId !== '' &&
		formData.contactPhone.trim() !== '' &&
		formData.lineItems.length > 0 &&
		!hasUnsatisfiedTargetIds &&
		!hasUnsatisfiedBusinessReasons &&
		!alternateAddressValidation.isInvalid

	// Build a list of unmet requirements straight from the current form data using
	// the same pure validators as submission. This stays accurate even before a
	// field has been blurred, so the submit-button tooltip can explain what to fix.
	const missingRequirements: string[] = []
	if ( !formData.requestingInventoryId ) {
		missingRequirements.push( 'Select a requesting branch' )
	}
	const phoneRequirement = validateContactPhone( formData.contactPhone )
	if ( phoneRequirement.isInvalid && phoneRequirement.error ) {
		missingRequirements.push( phoneRequirement.error )
	}
	const emailRequirement = validateCCEmail( formData.ccEmail )
	if ( emailRequirement.isInvalid && emailRequirement.error ) {
		missingRequirements.push( emailRequirement.error )
	}
	const notesRequirement = validateNotes( formData.notes )
	if ( notesRequirement.isInvalid && notesRequirement.error ) {
		missingRequirements.push( notesRequirement.error )
	}
	if ( alternateAddressValidation.isInvalid && alternateAddressValidation.error ) {
		missingRequirements.push( alternateAddressValidation.error )
	}
	const lineItemsRequirement = validateLineItems( formData.lineItems )
	if ( lineItemsRequirement.isInvalid && lineItemsRequirement.error ) {
		missingRequirements.push( lineItemsRequirement.error )
	}

	// Calculate total quantity across all line items (ignore completely blank line items)
	const totalQuantity = useCallback( () => {
		return formData.lineItems
			.filter( ( item ) => item.productId || ( item.quantity && item.quantity > 0 ) )
			.reduce( ( sum, lineItem ) => sum + ( lineItem.quantity || 0 ), 0 )
	}, [ formData.lineItems ] )()

	return {
		// Form data
		formData,
		validation: { ...validation, alternateAddress: alternateAddressValidation },
		isValid,
		missingRequirements,

		// Form field setters
		setRequestingInventoryId,
		setFulfillingInventoryId,
		setContactPhone,
		validateContactPhoneOnBlur,
		setCCEmail,
		validateCCEmailOnBlur,
		setNotes,
		setDeliverToAlternateAddress,
		setAlternateAddressField,

		// Line items management
		lineItems: formData.lineItems,
		addLineItem,
		removeLineItem,
		updateLineItem,

		// Computed values
		totalQuantity,

		// Form management
		resetForm,
		validateForm,
	}
}
